import { bindCommand, defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Profiler } from '../../../Profiler';
import { resolve } from '../../Container';
import { jiraImport } from '../../packages/atlassian/AtlassianCommands';
import { flexLayoutConfigure } from '../../packages/autoLayout/AutoLayoutCommands';
import { flip } from '../../packages/conceal/ConcealCommands';
import { connectorToolSpecialEquip } from '../../packages/connector/ConnectorCommands';
import { exportData } from '../../packages/dataExport/ExportCommands';
import { userWearHat } from '../../packages/hats/HatsCommands';
import { herdToMe } from '../../packages/herding/HerdingCommands';
import { gestureOpen } from '../../packages/presentation/GesturesCommands';
import { spotlightToggleSelf } from '../../packages/spotlight/SpotlightCommands';
import { cameraFrameContent, cameraZoomReset } from '../commands/CameraCommands';
import { copy, copyData, copyStyle, pasteStyle } from '../commands/ClipboardCommands';
import { lockToggle } from '../commands/ComponentCommands';
import { beginMutating, duplicate, escape } from '../commands/ManipulationCommands';
import { repeat, selectAll, selectOthers } from '../commands/SelectionCommands';
import { developerToggle } from '../developer/DeveloperCommands';
import { IsEditorPaletteOpen } from '../EditorPalette';
import { EditorSession } from '../EditorSession';
import { redo, undo } from '../history/HistoryCommands';
import { MyRoleInfo } from '../MyRoleInfo';
import { ProfilerOverlay } from '../ProfilerOverlay';
import { toolEquip, toolReset } from '../tooling/ToolCommands';
import { KeyMappings } from './KeyMappings';
import { ToolBehavior } from './ToolBehavior';

const openPalette = defineCommand(
    'openPalette',
    'Open Palette',
    (r) => {
        const session = r.call(EditorSession);
        session.update(IsEditorPaletteOpen, true);
    },
    []
);

const equipDebugTool = defineCommand(
    'equipDebugTool',
    'Equip Debug Tool',
    (r) => {
        const d = r.call(CommandDispatcher);
        d(toolEquip, ['debugTool']);
    },
    []
);

const toggleProfiler = defineCommand(
    'toggleProfiler',
    'Toggle Profiler',
    () => {
        ProfilerOverlay.toggle();
    },
    []
);

const enableProfiler = defineCommand(
    'enableProfiler',
    'Enable Profiler',
    () => {
        ProfilerOverlay.toggle();
        Profiler.setEnabled(true);
    },
    []
);

const disableProfiler = defineCommand(
    'disableProfiler',
    'Disable Profiler',
    () => {
        Profiler.setEnabled(false);
    },
    []
);

// const toggleDebugObjects = defineCommand(
//     'toggleDebugObjects',
//     'Toggle Debug Objects',
//     () => {
//         DebugOverlay.toggleObjects();
//     },
//     []
// );

const frameContent = bindCommand(cameraFrameContent, [200]);

export class CommonShortcutToolBehavior extends ToolBehavior {

    @resolve(EditorSession) private session: EditorSession;

    public getKeyMappings() {
        const role = this.session.get(MyRoleInfo);
        if (role.canParticipate) {
            return this.getParticipantKeyMappings();
        }
        else {
            return this.getViewerKeyMappings();
        }
    }

    private getViewerKeyMappings() {
        return KeyMappings.from(
            ['ESCAPE', escape],
            ['CTRL+NUMPAD_0', cameraZoomReset],
            ['CTRL+0', cameraZoomReset],
            ['CTRL+NUMPAD_1', frameContent],
            ['CTRL+1', frameContent],
        );
    }

    private getParticipantKeyMappings() {
        return KeyMappings.from(
            ['F2', developerToggle],
            ['ENTER', beginMutating],
            ['ESCAPE', escape],
            ['F', flip],
            ['R', repeat],
            ['U', flexLayoutConfigure],
            ['V', toolReset],
            ['W', gestureOpen],
            ['X', connectorToolSpecialEquip],
            ['CTRL+NUMPAD_0', cameraZoomReset],
            ['CTRL+0', cameraZoomReset],
            ['CTRL+NUMPAD_1', frameContent],
            ['CTRL+1', frameContent],
            ['CTRL+A', selectAll],
            ['CTRL+C', copy],
            ['CTRL+D', duplicate],
            ['CTRL+L', lockToggle],
            ['CTRL+M', spotlightToggleSelf],
            ['CTRL+S', exportData],
            ['CTRL+Y', redo],
            ['CTRL+SHIFT+Z', redo],
            ['CTRL+Z', undo],
            ['CTRL+SHIFT+A', selectOthers],
            ['CTRL+SHIFT+C', copyData],
            ['CTRL+ALT+C', copyStyle],
            ['SHIFT+C', copyStyle],
            ['CTRL+ALT+V', pasteStyle],
            ['SHIFT+V', pasteStyle],
            ['CTRL+SPACE', openPalette],
            ['CTRL+K', openPalette],
            ['SHIFT+B', herdToMe],
            ['SHIFT+H', userWearHat],
            ['SHIFT+J', jiraImport],
            // ['P', toggleProfiler],
            // ['I', enableProfiler],
            // ['O', disableProfiler],
            // ['Q', toggleHistoryPane], // TODO: Potential circular issue
            // ['KEY_1', toggleDebugObjects]
        );
    }
}
